import React, { createContext, useContext, useEffect, useState } from "react";

export const BACKGROUNDS = [
  // Light & Clean
  { id: "default", name: "Clean", gradient: "#F9FAFB", preview: "#F9FAFB" },
  { id: "pearl", name: "Pearl", gradient: "linear-gradient(135deg, #FAFAFA 0%, #F0F0F2 100%)", preview: "linear-gradient(135deg, #FAFAFA, #F0F0F2)" },
  { id: "champagne", name: "Champagne", gradient: "linear-gradient(135deg, #FAF0E6 0%, #F5DEB3 100%)", preview: "linear-gradient(135deg, #FAF0E6, #F5DEB3)" },

  // Soft pastels
  { id: "ocean", name: "Ocean", gradient: "linear-gradient(135deg, #E0F2FE 0%, #DBEAFE 100%)", preview: "linear-gradient(135deg, #E0F2FE, #DBEAFE)" },
  { id: "sunset", name: "Sunset", gradient: "linear-gradient(135deg, #FEF3C7 0%, #FED7AA 100%)", preview: "linear-gradient(135deg, #FEF3C7, #FED7AA)" },
  { id: "lavender", name: "Lavender", gradient: "linear-gradient(135deg, #EDE9FE 0%, #FCE7F3 100%)", preview: "linear-gradient(135deg, #EDE9FE, #FCE7F3)" },
  { id: "mint", name: "Mint", gradient: "linear-gradient(135deg, #D1FAE5 0%, #CFFAFE 100%)", preview: "linear-gradient(135deg, #D1FAE5, #CFFAFE)" },
  { id: "rose", name: "Rose", gradient: "linear-gradient(135deg, #FFE4E6 0%, #FCE7F3 100%)", preview: "linear-gradient(135deg, #FFE4E6, #FCE7F3)" },
  { id: "sky", name: "Sky", gradient: "linear-gradient(135deg, #DBEAFE 0%, #E0E7FF 100%)", preview: "linear-gradient(135deg, #DBEAFE, #E0E7FF)" },
  { id: "peach", name: "Peach", gradient: "linear-gradient(135deg, #FED7AA 0%, #FECACA 100%)", preview: "linear-gradient(135deg, #FED7AA, #FECACA)" },

  // Luxury / metallic
  { id: "gold", name: "Gold Dust", gradient: "linear-gradient(135deg, #FFF8DC 0%, #DAA520 100%)", preview: "linear-gradient(135deg, #FFF8DC, #DAA520)" },
  { id: "rosegold", name: "Rose Gold", gradient: "linear-gradient(135deg, #FFE4E1 0%, #B76E79 100%)", preview: "linear-gradient(135deg, #FFE4E1, #B76E79)" },
  { id: "platinum", name: "Platinum", gradient: "linear-gradient(135deg, #E5E4E2 0%, #B8B8B8 100%)", preview: "linear-gradient(135deg, #E5E4E2, #B8B8B8)" },
  { id: "copper", name: "Copper", gradient: "linear-gradient(135deg, #FFE4C4 0%, #B87333 100%)", preview: "linear-gradient(135deg, #FFE4C4, #B87333)" },

  // Rich / vibrant
  { id: "emerald", name: "Emerald", gradient: "linear-gradient(135deg, #D1FAE5 0%, #047857 100%)", preview: "linear-gradient(135deg, #D1FAE5, #047857)", dark: true },
  { id: "sapphire", name: "Sapphire", gradient: "linear-gradient(135deg, #DBEAFE 0%, #1E3A8A 100%)", preview: "linear-gradient(135deg, #DBEAFE, #1E3A8A)", dark: true },
  { id: "amethyst", name: "Amethyst", gradient: "linear-gradient(135deg, #EDE9FE 0%, #6D28D9 100%)", preview: "linear-gradient(135deg, #EDE9FE, #6D28D9)", dark: true },
  { id: "ruby", name: "Ruby", gradient: "linear-gradient(135deg, #FFE4E6 0%, #BE123C 100%)", preview: "linear-gradient(135deg, #FFE4E6, #BE123C)", dark: true },

  // Dark luxe
  { id: "midnight", name: "Midnight", gradient: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)", preview: "linear-gradient(135deg, #1E293B, #0F172A)", dark: true },
  { id: "obsidian", name: "Obsidian", gradient: "linear-gradient(135deg, #18181B 0%, #000000 100%)", preview: "linear-gradient(135deg, #18181B, #000000)", dark: true },
  { id: "noir", name: "Noir", gradient: "linear-gradient(135deg, #292524 0%, #1C1917 100%)", preview: "linear-gradient(135deg, #292524, #1C1917)", dark: true },
  { id: "burgundy", name: "Burgundy", gradient: "linear-gradient(135deg, #4C0519 0%, #2D0509 100%)", preview: "linear-gradient(135deg, #4C0519, #2D0509)", dark: true },
  { id: "forest", name: "Forest", gradient: "linear-gradient(135deg, #14532D 0%, #052E16 100%)", preview: "linear-gradient(135deg, #14532D, #052E16)", dark: true },

  // Tropical / aurora
  { id: "aurora", name: "Aurora", gradient: "linear-gradient(135deg, #A855F7 0%, #3B82F6 50%, #06B6D4 100%)", preview: "linear-gradient(135deg, #A855F7, #3B82F6, #06B6D4)", dark: true },
  { id: "tropical", name: "Tropical", gradient: "linear-gradient(135deg, #F472B6 0%, #FB923C 100%)", preview: "linear-gradient(135deg, #F472B6, #FB923C)" },
  { id: "northern", name: "Northern Lights", gradient: "linear-gradient(135deg, #1E3A8A 0%, #059669 50%, #7C3AED 100%)", preview: "linear-gradient(135deg, #1E3A8A, #059669, #7C3AED)", dark: true },
];

interface BgContextType {
  bgId: string;
  setBgId: (id: string) => void;
  bg: typeof BACKGROUNDS[0];
}

const BackgroundContext = createContext<BgContextType>({
  bgId: "default",
  setBgId: () => {},
  bg: BACKGROUNDS[0],
});

export function BackgroundProvider({ children }: { children: React.ReactNode }) {
  const [bgId, setBgIdState] = useState<string>(() => localStorage.getItem("bg") || "default");

  const bg = BACKGROUNDS.find(b => b.id === bgId) || BACKGROUNDS[0];

  function setBgId(id: string) {
    setBgIdState(id);
    localStorage.setItem("bg", id);
    applyBackground(id);
  }

  function applyBackground(id: string) {
    const selected = BACKGROUNDS.find(b => b.id === id) || BACKGROUNDS[0];
    // Set CSS variable that styles will use
    document.documentElement.style.setProperty("--app-bg", selected.gradient);
    document.body.style.background = selected.gradient;
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.minHeight = "100vh";

    // Add or remove a class on the html element so pages can override their bg
    if (id === "default") {
      document.documentElement.removeAttribute("data-bg");
    } else {
      document.documentElement.setAttribute("data-bg", id);
    }
  }

  useEffect(() => {
    applyBackground(bgId);
  }, [bgId]);

  return (
    <BackgroundContext.Provider value={{ bgId, setBgId, bg }}>
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackground() {
  return useContext(BackgroundContext);
}
